import { FC, RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Box } from '@mui/material'

import { HeaderGameIcon, HeaderGenreIcon } from '../../helpers/deltaHeaderIcons'
import { differentiationDelta, getPatternMixRows, getTopDifferentiatingPatterns } from '../../helpers/showcase'
import { useCompareStoryPatterns } from '../../hooks/useCompareStoryPatterns'
import { useUAInsightsMeta } from '../../hooks/useUAInsightsMeta'
import { useUATaxonomy } from '../../hooks/useUATaxonomy'
import type { StoryPattern } from '../../types'
import { EmptyStateCard } from '../EmptyStateCard/EmptyStateCard'
import { PatternMixChart } from '../PatternMixChart/PatternMixChart'
import { RisingStoryPatterns } from '../RisingStoryPatterns/RisingStoryPatterns'
import { SectionCard } from '../SectionCard/SectionCard'
import type { ShowcaseCrumb } from '../ShowcaseBreadcrumb/ShowcaseBreadcrumb'
import { ShowcaseBreadcrumb } from '../ShowcaseBreadcrumb/ShowcaseBreadcrumb'
import { StoryPatternDetailView } from '../StoryPatternDetailView/StoryPatternDetailView'
import { StoryPatternsTable } from '../StoryPatternsTable/StoryPatternsTable'
import { TableSkeleton } from '../TableSkeleton/TableSkeleton'

interface CompareStoryPatternsTabProps {
  /** The source game (left side of the matchup). */
  sourceGameId: string
  /** Comparison target game (game-vs-game), mutually exclusive with `targetGenreId`. */
  targetGameId?: string | null
  /** Comparison target genre (game-vs-genre), mutually exclusive with `targetGameId`. */
  targetGenreId?: string | null
  /** Display label of the comparison target, used in the differentiation column header. */
  targetLabel: string
  /** Display label of the source game, used in the Monthly Δ column header tooltip. */
  sourceLabel: string
  /** Source game's icon URL, shown before the "Monthly Δ" column header. */
  sourceIconUrl?: string | null
  /** Target game's icon URL (game-vs-game), shown before the target's Δ column header. Genre targets fall back to a category badge. */
  targetIconUrl?: string | null
  /**
   * The dialog's scrollable body. On a drill-down transition the tab resets
   * this container to the top (instantly, no animation) so the new view starts
   * in view rather than mid-scroll — mirroring the dialog's tab-change reset.
   */
  scrollContainerRef?: RefObject<HTMLElement | null>
}

/**
 * "Story Patterns" tab for the compare dialogs (game-vs-game and
 * game-vs-genre). Mirrors the game page's `GameStoryPatternsTab` — the source
 * game's narrative-pattern mix plus a game-scoped drill-down (reusing
 * `StoryPatternDetailView`) — but benchmarks each pattern against the
 * comparison *target* instead of the game's genre. The overview cards surface
 * the patterns the source game leans on most relative to the target ("Top
 * Differentiating"), and the table adds a differentiation column.
 *
 * Drill-down state is local component state (not URL params) so it stays
 * self-contained inside the dialog and never collides with the game page's own
 * Story Patterns tab `pattern`/`lens` query params underneath the overlay.
 */
export const CompareStoryPatternsTab: FC<CompareStoryPatternsTabProps> = ({
  sourceGameId,
  targetGameId,
  targetGenreId,
  targetLabel,
  sourceLabel,
  sourceIconUrl,
  targetIconUrl,
  scrollContainerRef,
}) => {
  const { t } = useTranslation('ua-insights')
  const { patternLabel } = useUATaxonomy()
  const { patternById } = useUAInsightsMeta()
  const { data, isLoading } = useCompareStoryPatterns(sourceGameId, targetGameId ?? null, targetGenreId ?? null)

  const [activePattern, setActivePattern] = useState<string | null>(null)
  const [activeLens, setActiveLens] = useState<string | null>(null)
  const topRef = useRef<HTMLDivElement | null>(null)

  const openPattern = useCallback((patternId: string, lensMotivationId?: string | null) => {
    setActivePattern(patternId)
    setActiveLens(lensMotivationId ?? null)
  }, [])

  const backToTable = useCallback(() => {
    setActivePattern(null)
    setActiveLens(null)
  }, [])

  // Clicking an associated-motivation chip applies it as the lens within the
  // current pattern view. Unlike the game page (which leaves for a standalone
  // Motivation view), the dialog has no in-context motivation view, so lensing
  // the same pattern is the natural, self-contained behaviour here.
  const applyLens = useCallback(
    (motivationId: string) => {
      if (activePattern) openPattern(activePattern, motivationId)
    },
    [activePattern, openPattern]
  )

  // Jump back to the top of the dialog's scroll container on any drill-down
  // transition so the new view starts in view rather than mid-scroll. Prefer
  // scrolling the dialog body directly (instant, no animation); fall back to
  // `scrollIntoView` only when the container ref isn't supplied.
  useEffect(() => {
    const raf = requestAnimationFrame(() => {
      const container = scrollContainerRef?.current
      if (container) container.scrollTo({ top: 0, behavior: 'auto' })
      else topRef.current?.scrollIntoView({ block: 'start', behavior: 'auto' })
    })
    return () => cancelAnimationFrame(raf)
  }, [activePattern, activeLens, scrollContainerRef])

  // Only surface patterns the source game actually uses (>=1 creative); a
  // zero-creative pattern exists in the taxonomy but isn't part of the game's
  // mix. Sub-patterns are pruned the same way.
  const gamePatterns = useMemo<StoryPattern[]>(
    () => (data ? data.patterns.filter((p) => p.creativesCount > 0).map((p) => ({ ...p, children: p.children?.filter((c) => c.creativesCount > 0) })) : []),
    [data]
  )

  const topDifferentiating = useMemo(() => getTopDifferentiatingPatterns(gamePatterns), [gamePatterns])

  // The source-vs-target mix is built from the full pattern list (not the
  // creatives>0 subset) so it can also surface patterns the target uses but the
  // source skips. The benchmark is the other game (game-vs-game) or the genre
  // (game-vs-genre).
  const patternMixRows = useMemo(() => (data ? getPatternMixRows(data.patterns, patternLabel) : []), [data, patternLabel])

  // The rising cards render `deltaPp` as their headline figure. Swap in the
  // differentiation delta (the source's monthly delta minus the target's
  // monthly delta) so the cards read as "where the source's own trend diverges
  // most from the target's trend" rather than a plain month-over-month change.
  const differentiatingCards = useMemo(() => topDifferentiating.map((p) => ({ ...p, deltaPp: differentiationDelta(p) })), [topDifferentiating])

  // Header icons: the source game's icon leads the Monthly Δ column; the
  // target's icon leads its own Δ column — a game icon for a game-vs-game
  // matchup, or a generic category badge when the target is a whole genre.
  const sourceIcon = useMemo(() => <HeaderGameIcon src={sourceIconUrl} />, [sourceIconUrl])

  // Memoised because `StoryPatternsTable` keys its row flattening and column
  // definitions off these objects: a fresh identity each render would rebuild
  // both on every parent render.
  const benchmark = useMemo(
    () => ({
      // Both delta columns read "Monthly Δ"; the leading icon (source vs target)
      // is what distinguishes whose month-over-month change each column shows, and
      // the header tooltip spells out the target by name.
      label: t('showcase_col_change'),
      icon: targetGameId != null ? <HeaderGameIcon src={targetIconUrl} alt={targetLabel} /> : <HeaderGenreIcon label={targetLabel} />,
      tooltip: t('compare_story_patterns_delta_tooltip', { name: targetLabel }),
      accessor: (p: StoryPattern) => p.benchmark?.deltaPp,
    }),
    [t, targetGameId, targetIconUrl, targetLabel]
  )

  // Trailing column: the gap between the source's Monthly Δ and the target's
  // Monthly Δ (i.e. `differentiationDelta`), so the table surfaces where the
  // two trends diverge. Zero for a game compared against itself.
  const difference = useMemo(
    () => ({
      label: t('compare_story_patterns_diff_label'),
      accessor: differentiationDelta,
    }),
    [t]
  )

  if (isLoading || !data) {
    return <TableSkeleton rows={8} columns={6} />
  }

  if (!gamePatterns.length) {
    return <EmptyStateCard>{t('compare_story_patterns_empty')}</EmptyStateCard>
  }

  if (activePattern) {
    const resolved = patternById(activePattern)
    const parent = resolved?.parent
    const crumbs: ShowcaseCrumb[] = [{ label: t('game_story_patterns_breadcrumb_root'), onClick: backToTable }]
    if (parent) crumbs.push({ label: patternLabel(parent), onClick: () => openPattern(parent.id, activeLens) })
    if (resolved) crumbs.push({ label: patternLabel(resolved.pattern) })

    return (
      <Box ref={topRef} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <ShowcaseBreadcrumb crumbs={crumbs} />
        <StoryPatternDetailView
          patternId={activePattern}
          lensMotivationId={activeLens}
          gameId={sourceGameId}
          onOpenPattern={openPattern}
          onOpenMotivation={applyLens}
        />
      </Box>
    )
  }

  return (
    <Box ref={topRef} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {patternMixRows.length > 0 && (
        <SectionCard title={t('pattern_mix_title', { game: sourceLabel, benchmark: targetLabel })}>
          <PatternMixChart rows={patternMixRows} gameName={sourceLabel} benchmarkLabel={targetLabel} onSelectPattern={(patternId) => openPattern(patternId)} />
        </SectionCard>
      )}

      {differentiatingCards.length > 0 && (
        <SectionCard title={t('compare_top_differentiating_patterns')}>
          <RisingStoryPatterns patterns={differentiatingCards} onSelect={(patternId) => openPattern(patternId)} showGames={false} />
        </SectionCard>
      )}

      <SectionCard title={t('showcase_creative_story_patterns')} disableBodyPadding>
        <StoryPatternsTable
          patterns={gamePatterns}
          firstColumnLabel={t('showcase_col_story_pattern')}
          onSelectPattern={(patternId) => openPattern(patternId)}
          changeColumnIcon={sourceIcon}
          changeColumnTooltip={t('compare_story_patterns_delta_tooltip', { name: sourceLabel })}
          benchmark={benchmark}
          difference={difference}
          hideTopGames
          disableCard
        />
      </SectionCard>
    </Box>
  )
}
